import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import Navigation from './Navigation';
import Footer from './Footer';
import turnstileConfig from '../config/turnstile';
import googleConfig from '../config/google';
import config from '../config/api';

const Register = () => {
  const navigate = useNavigate();
  const { user, loginWithToken } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
    agreeMarketing: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  // Load Turnstile
  useEffect(() => {
    if (user) return;

    const loadTurnstile = () => {
      if (!document.getElementById("turnstile-script")) {
        const script = document.createElement("script");
        script.id = "turnstile-script";
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        document.head.appendChild(script);
        script.onload = renderTurnstile;
      } else if (window.turnstile) { renderTurnstile(); }
    };
    const renderTurnstile = () => {
      if (turnstileRef.current && window.turnstile && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileConfig.siteKey,
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(""),
          theme: "dark",
          language: "vi"
        });
      }
    };
    loadTurnstile();
    return () => { if (widgetIdRef.current && window.turnstile) { window.turnstile.remove(widgetIdRef.current); widgetIdRef.current = null; } };
  }, [user]);

  if (user) {
    return null;
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'Vui lòng nhập họ';
    if (!formData.lastName.trim()) newErrors.lastName = 'Vui lòng nhập tên';
    if (!formData.email.trim()) newErrors.email = 'Vui lòng nhập email';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email không hợp lệ';
    if (!formData.password) newErrors.password = 'Vui lòng nhập mật khẩu';
    else if (formData.password.length < 8) newErrors.password = 'Mật khẩu phải có ít nhất 8 ký tự';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Vui lòng xác nhận mật khẩu';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Mật khẩu xác nhận không khớp';
    if (!formData.agreeTerms) newErrors.agreeTerms = 'Vui lòng đồng ý với điều khoản sử dụng';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!turnstileToken) { setErrors({ submit: "Vui lòng xác minh bạn không phải robot" }); return; }
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      navigate('/login', { state: { message: 'Đăng ký thành công! Vui lòng đăng nhập.', type: 'success' } });
    } catch (error) {
      setErrors({ submit: 'Có lỗi xảy ra. Vui lòng thử lại.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Google Sign-In
  const handleGoogleRegister = async () => {
    setGoogleLoading(true);
    setErrors({});

    try {
      // Load Google Identity Services
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
        });
      }

      // Initialize Google Sign-In
      window.google.accounts.id.initialize({
        client_id: googleConfig.clientId,
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Show Google Sign-In popup
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback: use popup
          window.google.accounts.oauth2.initTokenClient({
            client_id: googleConfig.clientId,
            scope: 'openid email profile',
            callback: async (response) => {
              if (response.access_token) {
                // Get user info and send to backend
                const userInfo = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
                  headers: { Authorization: `Bearer ${response.access_token}` }
                }).then(r => r.json());

                // Exchange for our JWT
                const backendResponse = await fetch(`${config.TASKS_API_URL}/auth/google/token`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    credential: response.access_token,
                    user_info: userInfo
                  })
                });

                if (backendResponse.ok) {
                  const data = await backendResponse.json();
                  await loginWithToken(data.access_token);
                  navigate('/dashboard', { replace: true });
                }
              }
            }
          }).requestAccessToken();
        }
      });
    } catch (error) {
      console.error('Google register error:', error);
      setErrors({ submit: 'Đăng ký Google thất bại. Vui lòng thử lại.' });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleCallback = async (response) => {
    try {
      if (response.credential) {
        // Send ID token to backend
        const backendResponse = await fetch(`${config.TASKS_API_URL}/auth/google/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: response.credential })
        });

        if (backendResponse.ok) {
          const data = await backendResponse.json();
          await loginWithToken(data.access_token);
          navigate('/dashboard', { replace: true });
        } else {
          const errorData = await backendResponse.json();
          setErrors({ submit: errorData.detail || 'Đăng ký Google thất bại' });
        }
      }
    } catch (error) {
      console.error('Google callback error:', error);
      setErrors({ submit: 'Đăng ký Google thất bại. Vui lòng thử lại.' });
    }
    setGoogleLoading(false);
  };

  const inputClass = "w-full pl-10 pr-4 py-3 bg-dark-800/50 border border-cyan-500/20 rounded-xl text-white placeholder-ocean-100/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all";
  const labelClass = "block text-sm font-medium text-ocean-100/80 mb-1";

  return (
    <div className="min-h-screen bg-dark-900">
      <Navigation />

      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 pt-24">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <Link to="/" className="flex items-center justify-center space-x-2 mb-6">
              <SparklesIcon className="w-10 h-10 text-cyan-400" />
              <span className="text-3xl font-bold"><span className="text-white">Smart</span><span className="text-cyan-400">era</span></span>
            </Link>
            <h2 className="text-3xl font-bold text-white mb-2">Tạo tài khoản mới</h2>
            <p className="text-ocean-100/60">Bắt đầu hành trình chuyển đổi số cùng Smartera</p>
          </div>

          {/* Form */}
          <div className="bg-dark-800/50 backdrop-blur-xl p-8 rounded-2xl border border-cyan-500/10">
            {/* Social Login */}
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={handleGoogleRegister}
                disabled={googleLoading}
                className="w-full flex items-center justify-center px-4 py-3 border border-cyan-500/20 rounded-xl hover:bg-cyan-500/10 transition-colors text-ocean-100/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {googleLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-400 mr-3"></div>
                    Đang xử lý...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Đăng ký với Google
                  </>
                )}
              </button>
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-cyan-500/20" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-dark-800/50 text-ocean-100/50">Hoặc đăng ký bằng email</span>
              </div>
            </div>

            {errors.submit && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2" />
                <span className="text-red-400 text-sm">{errors.submit}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Họ *</label>
                  <div className="relative">
                    <UserIcon className="w-5 h-5 text-cyan-400/50 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange}
                      className={`${inputClass} ${errors.firstName ? 'border-red-500/50' : ''}`} placeholder="Nguyễn" />
                  </div>
                  {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className={labelClass}>Tên *</label>
                  <div className="relative">
                    <UserIcon className="w-5 h-5 text-cyan-400/50 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange}
                      className={`${inputClass} ${errors.lastName ? 'border-red-500/50' : ''}`} placeholder="Văn A" />
                  </div>
                  {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={labelClass}>Email *</label>
                <div className="relative">
                  <EnvelopeIcon className="w-5 h-5 text-cyan-400/50 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                    className={`${inputClass} ${errors.email ? 'border-red-500/50' : ''}`} placeholder="email@example.com" />
                </div>
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Phone and Company */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Số điện thoại</label>
                  <div className="relative">
                    <PhoneIcon className="w-5 h-5 text-cyan-400/50 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                      className={inputClass} placeholder="+84 123 456 789" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Công ty</label>
                  <div className="relative">
                    <BuildingOfficeIcon className="w-5 h-5 text-cyan-400/50 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input type="text" name="company" value={formData.company} onChange={handleInputChange}
                      className={inputClass} placeholder="Tên công ty" />
                  </div>
                </div>
              </div>

              {/* Password */}
              <div>
                <label className={labelClass}>Mật khẩu *</label>
                <div className="relative">
                  <LockClosedIcon className="w-5 h-5 text-cyan-400/50 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange}
                    className={`${inputClass} pr-12 ${errors.password ? 'border-red-500/50' : ''}`} placeholder="Ít nhất 8 ký tự" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-cyan-400/50 hover:text-cyan-400">
                    {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div>
                <label className={labelClass}>Xác nhận mật khẩu *</label>
                <div className="relative">
                  <LockClosedIcon className="w-5 h-5 text-cyan-400/50 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange}
                    className={`${inputClass} pr-12 ${errors.confirmPassword ? 'border-red-500/50' : ''}`} placeholder="Nhập lại mật khẩu" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-cyan-400/50 hover:text-cyan-400">
                    {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>

              {/* Terms */}
              <div className="space-y-3">
                <div className="flex items-start">
                  <input type="checkbox" name="agreeTerms" checked={formData.agreeTerms} onChange={handleInputChange}
                    className="mt-1 h-4 w-4 rounded border-cyan-500/30 bg-dark-800 text-cyan-500 focus:ring-cyan-500/50" />
                  <label className="ml-2 text-sm text-ocean-100/70">
                    Tôi đồng ý với <Link to="/terms" className="text-cyan-400 hover:text-cyan-300">Điều khoản sử dụng</Link> và{' '}
                    <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300">Chính sách bảo mật</Link> *
                  </label>
                </div>
                {errors.agreeTerms && <p className="text-red-400 text-xs">{errors.agreeTerms}</p>}

                <div className="flex items-start">
                  <input type="checkbox" name="agreeMarketing" checked={formData.agreeMarketing} onChange={handleInputChange}
                    className="mt-1 h-4 w-4 rounded border-cyan-500/30 bg-dark-800 text-cyan-500 focus:ring-cyan-500/50" />
                  <label className="ml-2 text-sm text-ocean-100/70">Tôi muốn nhận thông tin về sản phẩm và dịch vụ mới</label>
                </div>
              </div>

              {/* Turnstile */}
              <div className="flex justify-center my-4">
                <div ref={turnstileRef}></div>
              </div>

              {/* Submit */}
              <button type="submit" disabled={isLoading || !turnstileToken}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-3 px-4 rounded-xl hover:from-cyan-400 hover:to-blue-500 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-glow-cyan/30 hover:shadow-glow-cyan">
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Đang tạo tài khoản...
                  </>
                ) : 'Tạo tài khoản'}
              </button>
            </form>

            {/* Login Link */}
            <div className="mt-6 text-center">
              <p className="text-ocean-100/60">
                Đã có tài khoản?{' '}
                <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium">Đăng nhập ngay</Link>
              </p>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center">
            <Link to="/" className="text-ocean-100/50 hover:text-cyan-400 transition-colors">← Quay lại trang chủ</Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Register;
